import React, { ReactNode, useEffect } from 'react';
import classNames from 'classnames';
import css from './CAPIConfigurations.module.css';
import emptyIcon from 'assets/fb-cart.svg';
import { Alert, Button, Status, Tooltip } from '@mm/components';
import { useHistory } from 'react-router-dom';
import { BotBuilderRoutes } from '../../../../../../../../routes/bot-builder-routes';
import { useSelector } from 'react-redux';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { useXRayConfigurationCRUDActions } from '@mm/state/actions/XRayConfiguration';
import {
  Grid,
  GridCell,
  GridLabels,
  GridRow,
} from '../../../../../../../../common/components/Grid/Grid';
import { TableLabel } from '../../../../../../../../common/components/TableGrid/TableGrid';
import {
  selectXRayConfig,
  hasConfiguredEvents,
  hasConfiguredFb,
} from '@mm/state/selectors/xrayConfigSelectors';
import { colors } from '@mm/utils';
import {
  facebookConversionApiEvents,
  facebookConversionApiVisibleEventsIds,
} from '../../../../FBConversionsApi/SetupForm/SetupForm';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { isDefaultFacebookConversionApiEventId } from '@mm/state/helpers/capi-helpers';
import { Tag } from 'antd';
import { uniqueId } from 'lodash';
import moment from 'moment';

type CAPIConfigurationProps = {
  className?: string;
  withHeader?: boolean;
  gridColumns?: string;
  styles?: {
    table?: string;
    tableHeader?: string;
    tableRows?: string;
  };
  remappedColumns?: {
    [key: string]: string | ReactNode;
  };
  emptyStateComponent?: ReactNode;
};

const gridTemplateColumns = `70% 15% 15%`;

const cells = [
  {
    label: 'Event',
    keyName: 'event',
    Cell: ({ item: { id, value } }) => (
      <GridCell key="event">
        {isDefaultFacebookConversionApiEventId(id) ? (
          <>{facebookConversionApiEvents.find(e => e.key === id)?.title}: </>
        ) : (
          <>
            <Tag color="blue">User event</Tag> {id}:
          </>
        )}

        <span className={css.eventPath}>{value}</span>
      </GridCell>
    ),
  },
  {
    label: 'Total Events Sent',
    keyName: 'total_sent',
    Cell: ({ item: { count } }) => (
      <GridCell key="total_sent">{count?.toLocaleString()}</GridCell>
    ),
  },
  {
    label: 'Status',
    keyName: 'status',
    Cell: ({ item: { active } }) => (
      <GridCell key="status">
        <Status
          className={css.status}
          status={active ? 'Active' : 'Not active'}
          indicatorSizePx={8}
          color={active ? colors.green6 : colors.grayLight}
        />
      </GridCell>
    ),
  },
];

export const CAPIConfigurations = ({
  className,
  withHeader = true,
  remappedColumns,
  gridColumns,
  styles,
  emptyStateComponent,
}: CAPIConfigurationProps) => {
  const history = useHistory();
  const containerClassName = classNames(className, css.container);

  const chatbotId = useSelector(activeChatbotIdSelector);

  const xRayConfigurationCRUDActions = useXRayConfigurationCRUDActions();

  const defaultConfiguration = useSelector(selectXRayConfig);

  const handleManageEventClick = () =>
    history.push(BotBuilderRoutes.capi.getLink({ chatbotId }));

  useEffect(() => {
    xRayConfigurationCRUDActions.getXRayEventsConfiguration({
      botId: chatbotId,
    });
  }, [chatbotId]);

  const tableData = Object.entries(defaultConfiguration?.configurations || {})
    .filter(([id, value]) => {
      const shouldBeVisible = isDefaultFacebookConversionApiEventId(id)
        ? facebookConversionApiVisibleEventsIds.includes(id)
        : true;
      const hasValue = value || defaultConfiguration?.eventsCounts[id];

      return shouldBeVisible && hasValue;
    })
    .map(([id, value]) => ({
      id,
      value,
      count: defaultConfiguration?.eventsCounts[id] || 0,
      active: defaultConfiguration?.active || false,
    }));

  const remappedCells = remappedColumns
    ? cells
        .map(cell => ({
          ...cell,
          label: remappedColumns?.[cell.keyName],
        }))
        .filter(cell => cell.label)
    : cells;

  return (
    <div className={containerClassName}>
      {withHeader && (
        <div className={css.header}>
          <span className={css.title}>
            SUPER-CAPI Event Match Booster
            <Tooltip
              css={{ container: css.tooltip }}
              color="dark"
              position="bottom"
              align="center"
              useHover
              withoutLine
              anchorElement={
                <QuestionCircleOutlined className={css.infoIcon} />
              }
            >
              Send event data directly to Facebooks event manager. Improve event
              match quality, lower ad costs, and attribute sales.{' '}
              <a
                className={css.learnMoreLink}
                href="https://customers.ai/help/l/en/article/d0uzm8hcap-share-your-brand-s-meta-dataset-information-with-customers-ai"
                target="_blank"
                rel="noreferrer"
              >
                Learn more
              </a>
            </Tooltip>
            <Tooltip
              anchorElement={
                <span className={css.tag}>Included for a limited time!</span>
              }
              css={{ container: css.tooltip }}
              color="dark"
              position="bottom"
              align="center"
              useHover
              withoutLine
            >
              Super-CAPI is FREE for a limited time on your plan
            </Tooltip>
          </span>
          <div className={css.actions}>
            <Button
              css={{ container: css.actionBtn }}
              color="secondary-blue"
              onClick={handleManageEventClick}
            >
              Manage Your Events
            </Button>
          </div>
        </div>
      )}

      {(!defaultConfiguration?.facebookErrors?.length || true) && (
        <div className={css.errors}>
          {defaultConfiguration?.facebookErrors?.map(err => (
            <Alert type="error" showCloseIcon={false} key={err.id}>
              <div>
                {err.severity && <Tag color="red">{err.severity}</Tag>}

                {err.updatedAt && (
                  <span>
                    <strong>
                      {moment(err.updatedAt).format('MM/DD/YYYY')}{' '}
                    </strong>
                  </span>
                )}
              </div>
              {err.originalMessage ? (
                <span>{err.originalMessage}</span>
              ) : (
                <span>
                  {err.details?.message && <span>err.details?.message</span>}
                </span>
              )}
            </Alert>
          ))}
        </div>
      )}

      <div className={css.steps}>
        {((!hasConfiguredEvents(defaultConfiguration) &&
          !hasConfiguredFb(defaultConfiguration)) ||
          !tableData.length) &&
          (emptyStateComponent ? (
            emptyStateComponent
          ) : (
            <div className={css.empty}>
              <div className={css.iconContainer}>
                <img className={css.icon} src={emptyIcon} alt="Cart" />
              </div>
              <Button
                css={{ container: css.ctaBtn }}
                color="blue"
                onClick={handleManageEventClick}
              >
                Manage Your Events
              </Button>
            </div>
          ))}

        {!!defaultConfiguration?.configurations &&
          (hasConfiguredFb(defaultConfiguration) ||
            hasConfiguredEvents(defaultConfiguration)) &&
          !!tableData?.length && (
            <Grid
              className={classNames(
                css.table,
                'integrations-grid',
                styles?.table,
              )}
            >
              <>
                <GridLabels
                  className={classNames(css.tableHeader, styles?.tableHeader)}
                  gridTemplateColumns={gridColumns || gridTemplateColumns}
                >
                  {remappedCells.map(cell => (
                    <TableLabel
                      key={(cell.keyName as string) || uniqueId(cell.keyName)}
                      dataItem={cell}
                      orderByAtTheEnd
                    />
                  ))}
                </GridLabels>

                <div className={classNames(css.tableRows, styles?.tableRows)}>
                  {tableData.map(({ id, value, count, active }) => (
                    <GridRow
                      key={id}
                      gridTemplateColumns={gridColumns || gridTemplateColumns}
                    >
                      {remappedCells.map(({ Cell, keyName }) => (
                        <Cell
                          key={keyName}
                          item={{
                            id,
                            value,
                            count,
                            active,
                          }}
                        />
                      ))}
                    </GridRow>
                  ))}
                </div>
              </>
            </Grid>
          )}
      </div>
    </div>
  );
};
